import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import isoWeek from 'dayjs/plugin/isoWeek';
import duration from 'dayjs/plugin/duration';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(relativeTime);
dayjs.extend(isoWeek);
dayjs.extend(duration);
dayjs.extend(utc);
dayjs.extend(timezone);

export { dayjs };

/**
 * The zone every timestamp is rendered in.
 *
 * Instants are stored in UTC and only ever converted for display, so a
 * timestamp means the same moment to everyone reading it — the alternative is
 * a deadline that lands at a different hour depending on who opens the page.
 *
 * Held in a module variable rather than threaded through every call: formatting
 * happens in hundreds of places, and a prop for it would be forgotten in some
 * of them, which is exactly where a wrong hour would hide.
 */
export const systemTimezone = (): string => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
};

let displayTimezone = systemTimezone();

/** Set once the signed-in user's preference is known. */
export const setDisplayTimezone = (zone?: string | null) => {
  displayTimezone = zone?.trim() || systemTimezone();
};

export const getDisplayTimezone = () => displayTimezone;

/**
 * Every zone this runtime knows, with its current offset.
 *
 * Read from the platform rather than shipped as a list: a bundled table goes
 * stale the next time a country changes its rules, and the browser's copy is
 * the one actually used to convert.
 */
export const timezoneOptions = (): Array<{ value: string; label: string; description: string }> => {
  const zones =
    typeof (Intl as { supportedValuesOf?: (key: string) => string[] }).supportedValuesOf ===
    'function'
      ? (Intl as unknown as { supportedValuesOf: (key: string) => string[] }).supportedValuesOf(
          'timeZone',
        )
      : [systemTimezone(), 'UTC'];

  return zones.map((zone) => {
    const offset = dayjs().tz(zone).format('Z');
    return {
      value: zone,
      label: zone.replace(/_/g, ' '),
      description: `GMT${offset} · ${dayjs().tz(zone).format('h:mm A')}`,
    };
  });
};

/** A dayjs instance in the display zone. */
export const inZone = (value?: string | Date | null) => dayjs(value).tz(displayTimezone);

/**
 * A wall-clock date and time, read in the display zone.
 *
 * `2026-08-25T14:30` on its own is not a moment — it is a moment only once a
 * zone is named. Left unnamed, it is resolved by whoever parses it: the browser
 * when it is compared here, the *server* when it is stored, and those two agree
 * only when the machines happen to share a zone. A local dev machine on IST and
 * a container on UTC do not, which is how a shift entered as 2:30pm came back
 * as 8:00pm.
 */
export const zoned = (date: string, time: string) =>
  dayjs.tz(`${date}T${time}`, displayTimezone);

/** A wall-clock `HH:mm` on a date, printed the way the app prints every time. */
export const formatClock = (date: string, time: string): string =>
  zoned(date, time).format('h:mm A');

/** The same, as the instant to send to the API. */
export const zonedInstant = (date: string, time: string): string => zoned(date, time).toISOString();

/** Today's date in the display zone, as `YYYY-MM-DD`. */
export const todayInZone = (): string => inZone(new Date()).format('YYYY-MM-DD');

/** A clock time for an <input type="time">, whose value is always 24-hour. */
export const inputTime = (value?: string | Date | null): string =>
  value ? inZone(value).format('HH:mm') : '';

/** "9:30 AM – 6:00 PM", or a single time when only one end is known. */
export const formatTimeRange = (
  from?: string | Date | null,
  to?: string | Date | null,
): string | null => {
  if (!from && !to) return null;
  if (from && to) return `${formatTime(from)} – ${formatTime(to)}`;
  return formatTime(from ?? to);
};

export const formatDate = (value?: string | Date | null, template = 'DD MMM YYYY'): string =>
  value ? inZone(value).format(template) : '—';

/** Times read as 12-hour with AM/PM throughout. */
export const formatTime = (value?: string | Date | null): string =>
  value ? inZone(value).format('h:mm A') : '—';

export const formatDateTime = (value?: string | Date | null): string =>
  value ? inZone(value).format('DD MMM YYYY, h:mm A') : '—';

export const formatRelative = (value?: string | Date | null): string =>
  value ? dayjs(value).fromNow() : '—';

/**
 * Monday of the ISO week containing `value`, as YYYY-MM-DD.
 *
 * Resolved in the display zone: a Sunday evening in Kolkata is already Monday
 * in UTC, and a timesheet that jumped a week at 5:30pm would be indefensible.
 */
export const weekStartOf = (value: string | Date = new Date()): string =>
  inZone(value).startOf('isoWeek').format('YYYY-MM-DD');

export const formatHours = (value: number): string => {
  if (!value) return '0h';
  // Timers store exact minutes, so a short run is a fraction of one.
  if (value < 1) return `${Math.max(1, Math.round(value * 60))}s`;
  const minutes = Math.round(value);
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (!hours) return `${mins}m`;
  return mins ? `${hours}h ${mins}m` : `${hours}h`;
};

/**
 * The same, for a figure already counted in hours.
 *
 * Estimates and logged totals are stored as decimal hours while time entries
 * are stored in minutes, and the two were printed differently — "1.5h" beside
 * "1h 30m" on the same row. Nobody reads a quarter of an hour as 0.25 unless a
 * spreadsheet made them.
 *
 * Negative values keep their sign rather than being formatted into nonsense:
 * remaining effort goes below zero, and "-2h 30m" is the answer there.
 */
export const formatHourValue = (hours: number): string => {
  const minutes = Math.round(hours * 60);
  return minutes < 0 ? `-${formatHours(-minutes)}` : formatHours(minutes);
};

/** Compact money for dashboards: 1.2Cr, 9.5L, 12.4k. */
export const formatCurrency = (value: number, currency = 'INR'): string => {
  const symbol = currency === 'INR' ? '₹' : currency === 'USD' ? '$' : `${currency} `;
  const abs = Math.abs(value);

  if (currency === 'INR') {
    if (abs >= 1e7) return `${symbol}${(value / 1e7).toFixed(2)}Cr`;
    if (abs >= 1e5) return `${symbol}${(value / 1e5).toFixed(2)}L`;
  } else if (abs >= 1e6) {
    return `${symbol}${(value / 1e6).toFixed(2)}M`;
  }
  if (abs >= 1e3) return `${symbol}${(value / 1e3).toFixed(1)}k`;
  return `${symbol}${value.toFixed(0)}`;
};

export const formatNumber = (value: number): string => new Intl.NumberFormat().format(value);

export const formatBytes = (bytes: number): string => {
  if (!bytes) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  const index = Math.min(units.length - 1, Math.floor(Math.log(bytes) / Math.log(1024)));
  return `${(bytes / 1024 ** index).toFixed(index ? 1 : 0)} ${units[index]}`;
};

/** How a due date should read and whether it needs attention. */
export const describeDueDate = (
  dueDate?: string | null,
  isDone = false,
): { label: string; tone: 'overdue' | 'today' | 'soon' | 'normal' | 'none' } => {
  if (!dueDate) return { label: '—', tone: 'none' };

  const due = dayjs(dueDate).startOf('day');
  const today = dayjs().startOf('day');
  const diff = due.diff(today, 'day');

  if (isDone) return { label: due.format('DD MMM'), tone: 'normal' };
  if (diff < 0) return { label: `${Math.abs(diff)}d overdue`, tone: 'overdue' };
  if (diff === 0) return { label: 'Today', tone: 'today' };
  if (diff === 1) return { label: 'Tomorrow', tone: 'soon' };
  if (diff <= 7) return { label: due.format('ddd DD MMM'), tone: 'soon' };
  return { label: due.format('DD MMM'), tone: 'normal' };
};

export const humanise = (value: string): string =>
  value.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

export const fullName = (user?: { firstName: string; lastName: string } | null): string =>
  user ? `${user.firstName} ${user.lastName}`.trim() : 'Unassigned';

/** Narrows a field that the API may return either populated or as a raw id. */
export const asRef = <T extends object>(value: T | string | undefined | null): T | null =>
  value && typeof value === 'object' ? (value as T) : null;


/**
 * A number field that has no meaning below zero.
 *
 * Kept as the string the input holds rather than a number, so clearing the
 * box still clears it — coercing here would turn an empty field into '0' and
 * fight whoever is halfway through typing. The spinner is stopped at zero by
 * min; this is for the minus key, which min does not stop.
 */
export const nonNegative = (value: string): string =>
  value === '' || Number(value) >= 0 ? value : '0';
